import { useEffect, useRef, useState } from "react";

interface CountUpProps {
  /** Final value to count to. */
  to: number;
  /** Animation length in milliseconds. */
  durationMs?: number;
  className?: string;
}

/** Decelerating curve — fast at the start, easing into the final number. */
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Animates a number from 0 up to `to`, the first time it scrolls into view.
 *
 * It waits for visibility rather than counting on mount because its only use —
 * the home hero's stat bar — sits at the bottom of a full-height hero, below
 * the fold on most screens. Counting on mount meant the animation was already
 * over by the time anyone scrolled down to it.
 *
 * Viewers who ask for reduced motion get the final value immediately, with no
 * animation frame. If IntersectionObserver is unavailable the value is shown
 * straight away rather than being left stuck at zero.
 */
const CountUp = ({ to, durationMs = 1600, className }: CountUpProps) => {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const [value, setValue] = useState(prefersReducedMotion ? to : 0);
  const elementRef = useRef<HTMLSpanElement>(null);
  const frameRef = useRef<number>();
  /** Guards against the observer restarting a count that already ran. */
  const startedRef = useRef(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setValue(to);
      return;
    }

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;

      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - start) / durationMs, 1);
        setValue(Math.round(easeOutCubic(progress) * to));
        if (progress < 1) frameRef.current = requestAnimationFrame(tick);
      };
      frameRef.current = requestAnimationFrame(tick);
    };

    const element = elementRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          run();
        }
      },
      // Wait until the number is properly on screen, not just clipping the edge.
      { threshold: 0.4 },
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, [to, durationMs, prefersReducedMotion]);

  return (
    <span ref={elementRef} className={className} style={{ fontVariantNumeric: "tabular-nums" }}>
      {value}
    </span>
  );
};

export default CountUp;
